import React, { useState } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import _isEmpty from 'lodash/isEmpty';
import { Icon } from 'grab-ui';

import {
  OTHER_ITEM,
  PAYMENT_ITEM_TYPE_LOCALES,
  PAYMENT_METHOD_TYPE,
} from '../../../constants/payment';
import {
  absAmount,
  formatPrice,
  isNegativeAmount,
} from '../../../common/utils/currency';
import cashV2Svg from '../../../public/images/cash-v2.svg';
import grabpaySvg from '../../../public/images/grabpay.svg';
import checkedSvg from '../../../public/images/checked-round.svg';
import styles from './pod-item.less';
import PayOnDeliverySubItemsModal from './pod-sub-items-modal';

const PayOnDeliveryItem = ({ item, cashlessPaymentCompleted, symbol }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  if (!item) {
    return null;
  }

  const isCashless = item.paymentMethod === PAYMENT_METHOD_TYPE.CASHLESS;
  const title = I18n.t(
    PAYMENT_ITEM_TYPE_LOCALES[item.type] ||
      PAYMENT_ITEM_TYPE_LOCALES[OTHER_ITEM],
  );

  const cashlessSubtitle = cashlessPaymentCompleted
    ? I18n.t('paymentDetails.paid')
    : I18n.t('paymentDetails.payInApp');

  const nonCashlessSubtitle = isNegativeAmount(item.amount)
    ? I18n.t('paymentDetails.collectCash')
    : I18n.t('paymentDetails.payCash');

  const subtitle = isCashless ? cashlessSubtitle : nonCashlessSubtitle;

  const showPaidIcon = isCashless && cashlessPaymentCompleted;

  const hasSubItems = !_isEmpty(item.subItems);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {isCashless ? (
          <img
            data-testid={`grabpay-image`}
            className={styles.image}
            src={grabpaySvg}
            alt="grabpay"
          />
        ) : (
          <img
            data-testid={`cash-image`}
            className={styles.image}
            src={cashV2Svg}
            alt="cash"
          />
        )}
        <div className={styles.textWrapper}>
          <p data-testid={`item-title`} className={styles.title}>
            {title}
            {hasSubItems && (
              <span className={styles.infoIcon}>
                <Icon type="info-circle" onClick={openModal} />
              </span>
            )}
          </p>
          <div className={styles.subtitleWrapper}>
            <p data-testid={`item-subtitle`} className={styles.subtitle}>
              {subtitle}
            </p>
            {showPaidIcon && (
              <img
                data-testid={`paid-icon`}
                className={styles.icon}
                src={checkedSvg}
                alt="paid"
              />
            )}
          </div>
        </div>
      </div>

      <p data-testid={`item-amount`} className={styles.amount}>
        {formatPrice({ amount: absAmount(item.amount), symbol })}
      </p>

      {hasSubItems && (
        <PayOnDeliverySubItemsModal
          feeType={item.type}
          subItems={item.subItems}
          symbol={symbol}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

PayOnDeliveryItem.propTypes = {
  item: PropTypes.shape({
    type: PropTypes.string,
    amount: PropTypes.string,
    paymentMethod: PropTypes.string,
    subItems: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        amount: PropTypes.string,
        paymentMethod: PropTypes.string,
      }),
    ),
  }),
  symbol: PropTypes.string,
  cashlessPaymentCompleted: PropTypes.bool,
};

PayOnDeliveryItem.defaultProps = {
  item: null,
  symbol: '',
  cashlessPaymentCompleted: false,
};

export default PayOnDeliveryItem;
